import axios from 'axios';
import { 
  Transaction, 
  TransactionCreateData, 
  TransactionUpdateData, 
  TransactionsResponse, 
  TransactionFilters,
  TransactionStats,
  TransactionPayment,
  PaymentFormData
} from '@/types/transaction';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

interface GetTransactionsParams {
  page?: number;
  limit?: number;
  search?: string;
  tipo?: string;
  estado?: string;
  cliente_id?: number;
  vendedor_id?: number;
  date_from?: string;
  date_to?: string;
}

class TransactionService {
  async getTransactions(params: GetTransactionsParams = {}): Promise<TransactionsResponse> {
    console.log('📋 TransactionService: Getting transactions', params);
    try {
      const response = await axios.get(`${API_URL}/transactions`, { params });
      console.log('✅ TransactionService: Transactions retrieved', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ TransactionService: Error getting transactions', error.response?.data || error.message);
      throw error;
    }
  }

  async getQuotations(params: GetTransactionsParams = {}): Promise<TransactionsResponse> {
    console.log('📋 TransactionService: Getting quotations', params);
    try {
      const response = await axios.get(`${API_URL}/transactions/quotations`, { params });
      console.log('✅ TransactionService: Quotations retrieved', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ TransactionService: Error getting quotations', error.response?.data || error.message);
      throw error;
    }
  }

  async getSales(params: GetTransactionsParams = {}): Promise<TransactionsResponse> {
    console.log('📋 TransactionService: Getting sales', params);
    try {
      const response = await axios.get(`${API_URL}/transactions/sales`, { params });
      console.log('✅ TransactionService: Sales retrieved', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ TransactionService: Error getting sales', error.response?.data || error.message);
      throw error;
    }
  }

  async getTransactionById(id: number): Promise<Transaction> {
    console.log('📄 TransactionService: Getting transaction by ID', { id });
    try {
      const response = await axios.get(`${API_URL}/transactions/${id}`);
      console.log('✅ TransactionService: Transaction retrieved', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ TransactionService: Error getting transaction by ID', error.response?.data || error.message);
      throw error;
    }
  }

  async createTransaction(transactionData: TransactionCreateData): Promise<Transaction> {
    console.log('➕ TransactionService: Creating transaction', { 
      tipo: transactionData.tipo, 
      cliente_id: transactionData.cliente_id 
    });
    try {
      const response = await axios.post(`${API_URL}/transactions`, transactionData);
      console.log('✅ TransactionService: Transaction created', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ TransactionService: Error creating transaction', error.response?.data || error.message);
      throw error;
    }
  }

  async updateTransaction(id: number, transactionData: TransactionUpdateData): Promise<Transaction> {
    console.log('✏️ TransactionService: Updating transaction', { 
      id, 
      estado: transactionData.estado 
    });
    try {
      const response = await axios.put(`${API_URL}/transactions/${id}`, transactionData);
      console.log('✅ TransactionService: Transaction updated', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ TransactionService: Error updating transaction', error.response?.data || error.message);
      throw error;
    }
  }

  async deleteTransaction(id: number): Promise<void> {
    console.log('🗑️ TransactionService: Deleting transaction', { id });
    console.log('🔑 TransactionService: Current auth header:', axios.defaults.headers.common['Authorization']);
    try {
      const response = await axios.delete(`${API_URL}/transactions/${id}`);
      console.log('✅ TransactionService: Transaction deleted', response.data);
    } catch (error: any) {
      console.log('❌ TransactionService: Error deleting transaction');
      console.log('Status:', error.response?.status);
      console.log('Response:', error.response?.data);
      console.log('Full error:', error);
      throw error;
    }
  }

  async changeStatus(id: number, newStatus: string): Promise<Transaction> {
    console.log('🔄 TransactionService: Changing transaction status', { id, newStatus });
    try {
      const response = await axios.patch(`${API_URL}/transactions/${id}/status`, { estado: newStatus });
      console.log('✅ TransactionService: Transaction status changed', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ TransactionService: Error changing transaction status', error.response?.data || error.message);
      throw error;
    }
  }

  async convertToSale(quotationId: number): Promise<Transaction> {
    console.log('🔄 TransactionService: Converting quotation to sale', { quotationId });
    try {
      const response = await axios.post(`${API_URL}/transactions/${quotationId}/convert-to-sale`);
      console.log('✅ TransactionService: Quotation converted to sale', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ TransactionService: Error converting quotation to sale', error.response?.data || error.message);
      throw error;
    }
  }

  async addPayment(transactionId: number, paymentData: PaymentFormData): Promise<TransactionPayment> {
    console.log('💰 TransactionService: Adding payment', { 
      transactionId, 
      metodo: paymentData.metodo,
      monto: paymentData.monto 
    });
    try {
      const response = await axios.post(`${API_URL}/transactions/${transactionId}/payments`, paymentData);
      console.log('✅ TransactionService: Payment added', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ TransactionService: Error adding payment', error.response?.data || error.message);
      throw error;
    }
  }

  async markAsPaid(transactionId: number): Promise<Transaction> {
    console.log('💰 TransactionService: Marking transaction as paid', { transactionId });
    try {
      const response = await axios.patch(`${API_URL}/transactions/${transactionId}/mark-paid`);
      console.log('✅ TransactionService: Transaction marked as paid', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ TransactionService: Error marking transaction as paid', error.response?.data || error.message);
      throw error;
    }
  }

  async getStats(): Promise<TransactionStats> {
    console.log('📊 TransactionService: Getting transaction statistics');
    try {
      const response = await axios.get(`${API_URL}/transactions/stats`);
      console.log('✅ TransactionService: Transaction stats retrieved', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ TransactionService: Error getting transaction stats', error.response?.data || error.message);
      throw error;
    }
  }

  async getRecentTransactions(limit: number = 5, tipo?: string): Promise<Transaction[]> {
    console.log('📄 TransactionService: Getting recent transactions', { limit, tipo });
    try {
      const params: any = { limit };
      if (tipo) params.tipo = tipo;

      const response = await axios.get(`${API_URL}/transactions/recent`, {
        params
      });
      console.log('✅ TransactionService: Recent transactions retrieved', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ TransactionService: Error getting recent transactions', error.response?.data || error.message);
      throw error;
    }
  }

  async generatePDF(id: number): Promise<Blob> {
    console.log('📄 TransactionService: Generating PDF', { id });
    try {
      const response = await axios.get(`${API_URL}/transactions/${id}/pdf`, {
        responseType: 'blob'
      });
      console.log('✅ TransactionService: PDF generated', response.data);
      return response.data;
    } catch (error: any) {
      console.log('❌ TransactionService: Error generating PDF', error.response?.data || error.message);
      throw error;
    }
  }

  async sendQuotationByEmail(id: number, data: { email?: string; customMessage?: string }): Promise<void> {
    console.log('📧 TransactionService: Sending quotation by email', { id, email: data.email });
    try {
      const response = await axios.post(`${API_URL}/transactions/${id}/send-email`, data);
      console.log('✅ TransactionService: Quotation sent by email', response.data);
    } catch (error: any) {
      console.log('❌ TransactionService: Error sending quotation by email', error.response?.data || error.message);
      throw error;
    }
  }


  // Helper methods for validation and formatting
  validateTransactionData(transactionData: TransactionCreateData): string[] {
    const errors: string[] = [];

    if (!transactionData.cliente_id) {
      errors.push('El cliente es requerido');
    }

    if (!transactionData.tipo) {
      errors.push('El tipo de transacción es requerido');
    }

    if (!transactionData.items || transactionData.items.length === 0) {
      errors.push('Debe agregar al menos un artículo');
    }

    transactionData.items?.forEach((item, index) => {
      if (!item.item_id) {
        errors.push(`El artículo en la línea ${index + 1} es requerido`);
      }
      if (!item.cantidad || item.cantidad <= 0) {
        errors.push(`La cantidad en la línea ${index + 1} debe ser mayor a 0`);
      }
      if (!item.precio_unitario || item.precio_unitario < 0) {
        errors.push(`El precio unitario en la línea ${index + 1} debe ser válido`);
      }
      if (item.descuento < 0 || item.descuento > 100) {
        errors.push(`El descuento en la línea ${index + 1} debe estar entre 0 y 100`);
      }
    });

    return errors;
  }

  validatePaymentData(paymentData: PaymentFormData): string[] {
    const errors: string[] = [];

    if (!paymentData.metodo) {
      errors.push('El método de pago es requerido');
    }

    if (!paymentData.monto || paymentData.monto <= 0) {
      errors.push('El monto debe ser mayor a 0');
    }

    return errors;
  }

  // Format methods
  formatTransactionType(tipo: string): string {
    return tipo === 'COTIZACION' ? 'Cotización' : 'Venta';
  }

  formatQuotationStatus(estado: string): string {
    const statuses: { [key: string]: string } = {
      'CREADA': 'Creada',
      'ENVIADA': 'Enviada',
      'ACEPTADA': 'Aceptada',
      'RECHAZADA': 'Rechazada',
      'VENCIDA': 'Vencida',
      'FINALIZADA': 'Finalizada'
    };
    return statuses[estado] || estado;
  }

  formatSaleStatus(estado: string): string {
    const statuses: { [key: string]: string } = {
      'PENDIENTE': 'Pendiente',
      'PAGADA': 'Pagada',
      'REEMBOLSADA': 'Reembolsada',
      'ANULADA': 'Anulada'
    };
    return statuses[estado] || estado;
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-GT', {
      style: 'currency',
      currency: 'GTQ',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  formatPaymentMethod(metodo: string): string {
    const methods: { [key: string]: string } = {
      'EFECTIVO': 'Efectivo',
      'TARJETA': 'Tarjeta',
      'TRANSFERENCIA': 'Transferencia',
      'CHEQUE': 'Cheque',
      'OTRO': 'Otro'
    };
    return methods[metodo] || metodo;
  }

  // Business logic helpers
  calculateSubtotal(items: any[]): number {
    return items.reduce((sum, item) => {
      const lineTotal = item.cantidad * item.precio_unitario;
      const discount = lineTotal * (item.descuento / 100);
      return sum + (lineTotal - discount);
    }, 0);
  }

  calculateTaxes(items: any[]): number {
    return items.reduce((sum, item) => {
      const lineTotal = item.cantidad * item.precio_unitario;
      const discount = lineTotal * (item.descuento / 100);
      const subtotal = lineTotal - discount;
      return sum + (subtotal * (item.tasa_impuesto / 100));
    }, 0);
  }

  calculateTotal(items: any[], globalDiscount: number = 0): number {
    const subtotal = this.calculateSubtotal(items);
    const taxes = this.calculateTaxes(items);
    const globalDiscountAmount = subtotal * (globalDiscount / 100);
    return subtotal + taxes - globalDiscountAmount;
  }

  // Filter and sort utilities
  filterTransactions(transactions: Transaction[], filters: TransactionFilters): Transaction[] {
    return transactions.filter(transaction => {
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch =
          transaction.numero.toLowerCase().includes(searchLower) ||
          transaction.cliente?.name?.toLowerCase().includes(searchLower) ||
          transaction.cliente?.doc_number?.toLowerCase().includes(searchLower) ||
          transaction.customer?.name?.toLowerCase().includes(searchLower) ||
          transaction.customer?.doc_number?.toLowerCase().includes(searchLower);

        if (!matchesSearch) return false;
      }

      if (filters.tipo && filters.tipo.length > 0) {
        if (transaction.tipo !== filters.tipo) return false;
      }

      if (filters.estado && filters.estado !== '') {
        if (transaction.estado !== filters.estado) return false;
      }

      if (filters.cliente_id != null) {
        if (transaction.cliente_id !== filters.cliente_id) return false;
      }

      if (filters.vendedor_id != null) {
        if (transaction.vendedor_id !== filters.vendedor_id) return false;
      }

      if (filters.date_from) {
        if (new Date(transaction.created_at) < new Date(filters.date_from)) return false;
      }

      if (filters.date_to) {
        if (new Date(transaction.created_at) > new Date(filters.date_to)) return false;
      }

      return true;
    });
  }

  sortTransactions(transactions: Transaction[], sortBy: keyof Transaction, sortOrder: 'asc' | 'desc' = 'desc'): Transaction[] {
    return [...transactions].sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];

      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortOrder === 'asc' ? -1 : 1;
      if (bVal == null) return sortOrder === 'asc' ? 1 : -1;

      if (aVal === bVal) return 0;

      const result = aVal < bVal ? -1 : 1;
      return sortOrder === 'asc' ? result : -result;
    });
  }

  // Get monthly sales data for charts
  async getMonthlySalesData(): Promise<{ labels: string[], data: number[] }> {
    console.log('📊 TransactionService: Getting monthly sales data');
    try {
      // Try to get data from backend endpoint
      const response = await axios.get(`${API_URL}/transactions/monthly_sales`);
      console.log('✅ TransactionService: Monthly sales data retrieved from API', response.data);
      return response.data.data;
    } catch (error: any) {
      console.warn('⚠️ Monthly sales data not available from API, using fallback', error.response?.data || error.message);
      
      try {
        // Fallback: Generate data from existing transactions
        const transactionsResponse = await this.getTransactions({ limit: 1000 });
        const salesData = this.generateMonthlySalesFromTransactions(transactionsResponse.transactions);
        console.log('✅ TransactionService: Monthly sales data generated from transactions', salesData);
        return salesData;
      } catch (fallbackError: any) {
        console.error('❌ TransactionService: Failed to get fallback data', fallbackError);
        // Return empty data structure if everything fails
        const labels = this.generateMonthLabels();
        return { labels, data: new Array(12).fill(0) };
      }
    }
  }

  // Get weekly sales data for charts
  async getWeeklySalesData(): Promise<{ labels: string[], data: number[] }> {
    console.log('📊 TransactionService: Getting weekly sales data');
    try {
      // Try to get data from backend endpoint
      const response = await axios.get(`${API_URL}/transactions/weekly_sales`);
      console.log('✅ TransactionService: Weekly sales data retrieved from API', response.data);
      return response.data.data;
    } catch (error: any) {
      console.warn('⚠️ Weekly sales data not available from API, using fallback', error.response?.data || error.message);
      
      try {
        // Fallback: Generate data from existing transactions
        const transactionsResponse = await this.getTransactions({ limit: 1000 });
        const salesData = this.generateWeeklySalesFromTransactions(transactionsResponse.transactions);
        console.log('✅ TransactionService: Weekly sales data generated from transactions', salesData);
        return salesData;
      } catch (fallbackError: any) {
        console.error('❌ TransactionService: Failed to get weekly fallback data', fallbackError);
        // Return empty data structure if everything fails
        const labels = this.generateWeekLabels();
        return { labels, data: new Array(12).fill(0) };
      }
    }
  }

  // Get yearly sales data for charts
  async getYearlySalesData(): Promise<{ labels: string[], data: number[] }> {
    console.log('📊 TransactionService: Getting yearly sales data');
    try {
      // Try to get data from backend endpoint
      const response = await axios.get(`${API_URL}/transactions/yearly_sales`);
      console.log('✅ TransactionService: Yearly sales data retrieved from API', response.data);
      return response.data.data;
    } catch (error: any) {
      console.warn('⚠️ Yearly sales data not available from API, using fallback', error.response?.data || error.message);
      
      try {
        // Fallback: Generate data from existing transactions
        const transactionsResponse = await this.getTransactions({ limit: 1000 });
        const salesData = this.generateYearlySalesFromTransactions(transactionsResponse.transactions);
        console.log('✅ TransactionService: Yearly sales data generated from transactions', salesData);
        return salesData;
      } catch (fallbackError: any) {
        console.error('❌ TransactionService: Failed to get yearly fallback data', fallbackError);
        // Return empty data structure if everything fails
        const labels = this.generateYearLabels();
        return { labels, data: new Array(5).fill(0) };
      }
    }
  }

  // Generate month labels helper
  private generateMonthLabels(): string[] {
    const months = [];
    const currentDate = new Date();
    
    for (let i = 11; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      months.push(date.toLocaleDateString('es-ES', { 
        month: 'short', 
        year: '2-digit' 
      }));
    }
    
    return months;
  }

  // Generate week labels helper (last 12 weeks)
  private generateWeekLabels(): string[] {
    const weeks = [];
    const currentDate = new Date();
    
    for (let i = 11; i >= 0; i--) {
      const date = new Date(currentDate.getTime() - (i * 7 * 24 * 60 * 60 * 1000));
      const startOfWeek = new Date(date.setDate(date.getDate() - date.getDay()));
      const endOfWeek = new Date(startOfWeek.getTime() + (6 * 24 * 60 * 60 * 1000));
      
      weeks.push(`${startOfWeek.getDate()}/${startOfWeek.getMonth() + 1} - ${endOfWeek.getDate()}/${endOfWeek.getMonth() + 1}`);
    }
    
    return weeks;
  }

  // Generate year labels helper (last 5 years)
  private generateYearLabels(): string[] {
    const years = [];
    const currentYear = new Date().getFullYear();
    
    for (let i = 4; i >= 0; i--) {
      years.push((currentYear - i).toString());
    }
    
    return years;
  }

  // Generate monthly sales data from transactions
  private generateMonthlySalesFromTransactions(transactions: Transaction[]): { labels: string[], data: number[] } {
    console.log('📈 Generating monthly sales from transactions:', {
      totalTransactions: transactions.length,
      salesTransactions: transactions.filter(t => t.tipo === 'VENTA').length,
      paidSales: transactions.filter(t => t.tipo === 'VENTA' && t.estado === 'PAGADA').length
    });

    const monthlyData: { [key: string]: number } = {};
    const currentDate = new Date();
    
    // Initialize last 12 months
    for (let i = 11; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthlyData[key] = 0;
    }

    // Aggregate sales by month
    const salesTransactions = transactions.filter(t => t.tipo === 'VENTA' && t.estado === 'PAGADA');
    console.log('💰 Processing paid sales:', salesTransactions.map(t => ({
      id: t.id,
      numero: t.numero,
      total: t.total,
      created_at: t.created_at,
      estado: t.estado
    })));

    salesTransactions.forEach(transaction => {
      const date = new Date(transaction.created_at);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (monthlyData.hasOwnProperty(key)) {
        monthlyData[key] += transaction.total || 0;
        console.log(`📅 Adding Q${transaction.total} to ${key}, new total: Q${monthlyData[key]}`);
      }
    });

    // Convert to chart format
    const labels: string[] = [];
    const data: number[] = [];
    
    for (let i = 11; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('es-ES', { 
        month: 'short', 
        year: '2-digit' 
      });
      
      labels.push(label);
      data.push(monthlyData[key]);
    }

    console.log('📊 Final monthly sales data:', { labels, data, monthlyData });
    return { labels, data };
  }

  // Generate weekly sales data from transactions
  private generateWeeklySalesFromTransactions(transactions: Transaction[]): { labels: string[], data: number[] } {
    console.log('📈 Generating weekly sales from transactions:', {
      totalTransactions: transactions.length,
      salesTransactions: transactions.filter(t => t.tipo === 'VENTA').length,
      paidSales: transactions.filter(t => t.tipo === 'VENTA' && t.estado === 'PAGADA').length
    });

    const weeklyData: { [key: string]: number } = {};
    const currentDate = new Date();
    
    // Initialize last 12 weeks
    for (let i = 11; i >= 0; i--) {
      const date = new Date(currentDate.getTime() - (i * 7 * 24 * 60 * 60 * 1000));
      const startOfWeek = new Date(date.setDate(date.getDate() - date.getDay()));
      const key = `${startOfWeek.getFullYear()}-W${this.getWeekNumber(startOfWeek)}`;
      weeklyData[key] = 0;
    }

    // Aggregate sales by week
    const salesTransactions = transactions.filter(t => t.tipo === 'VENTA' && t.estado === 'PAGADA');
    
    salesTransactions.forEach(transaction => {
      const date = new Date(transaction.created_at);
      const startOfWeek = new Date(date.setDate(date.getDate() - date.getDay()));
      const key = `${startOfWeek.getFullYear()}-W${this.getWeekNumber(startOfWeek)}`;
      
      if (weeklyData.hasOwnProperty(key)) {
        weeklyData[key] += transaction.total || 0;
      }
    });

    // Convert to chart format
    const labels = this.generateWeekLabels();
    const data: number[] = [];
    
    for (let i = 11; i >= 0; i--) {
      const date = new Date(currentDate.getTime() - (i * 7 * 24 * 60 * 60 * 1000));
      const startOfWeek = new Date(date.setDate(date.getDate() - date.getDay()));
      const key = `${startOfWeek.getFullYear()}-W${this.getWeekNumber(startOfWeek)}`;
      data.push(weeklyData[key] || 0);
    }

    console.log('📊 Final weekly sales data:', { labels, data, weeklyData });
    return { labels, data };
  }

  // Generate yearly sales data from transactions
  private generateYearlySalesFromTransactions(transactions: Transaction[]): { labels: string[], data: number[] } {
    console.log('📈 Generating yearly sales from transactions:', {
      totalTransactions: transactions.length,
      salesTransactions: transactions.filter(t => t.tipo === 'VENTA').length,
      paidSales: transactions.filter(t => t.tipo === 'VENTA' && t.estado === 'PAGADA').length
    });

    const yearlyData: { [key: string]: number } = {};
    const currentYear = new Date().getFullYear();
    
    // Initialize last 5 years
    for (let i = 4; i >= 0; i--) {
      const year = (currentYear - i).toString();
      yearlyData[year] = 0;
    }

    // Aggregate sales by year
    const salesTransactions = transactions.filter(t => t.tipo === 'VENTA' && t.estado === 'PAGADA');
    
    salesTransactions.forEach(transaction => {
      const date = new Date(transaction.created_at);
      const year = date.getFullYear().toString();
      
      if (yearlyData.hasOwnProperty(year)) {
        yearlyData[year] += transaction.total || 0;
      }
    });

    // Convert to chart format
    const labels = this.generateYearLabels();
    const data = labels.map(year => yearlyData[year] || 0);

    console.log('📊 Final yearly sales data:', { labels, data, yearlyData });
    return { labels, data };
  }

  // Helper method to get week number
  private getWeekNumber(date: Date): number {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  }
}

export const transactionService = new TransactionService();